import * as Sentry from "@sentry/react";

let hasInitialized = false;

const KNOWN_NON_APP_REJECTION_PATTERNS = [
  /^(Non-Error promise rejection captured with value: )?Object Not Found Matching Id:\d+, MethodName:update, ParamCount:\d+$/,
];

const GOOGLE_MAPS_MODULE_LOAD_ERROR_PATTERN =
  /^Could not load "(util|marker|onion|controls|infowindow)"\.$/;

// Generic browser wording for a fetch that never completed (dropped mobile/
// WebView connectivity, request aborted by navigation, transient DNS/CORS).
const NETWORK_FETCH_FAILURE_PATTERN = /^(Failed to fetch|Load failed)$/;

function isKnownNonAppPromiseRejection(event: Sentry.Event): boolean {
  const exceptionValue = event.exception?.values?.[0]?.value;

  if (typeof exceptionValue !== "string") {
    return false;
  }

  return KNOWN_NON_APP_REJECTION_PATTERNS.some((pattern) =>
    pattern.test(exceptionValue)
  );
}

function isGoogleMapsModuleLoadError(event: Sentry.Event): boolean {
  const exception = event.exception?.values?.[0];
  const exceptionValue = exception?.value;

  if (
    exception?.type !== "Error" ||
    typeof exceptionValue !== "string" ||
    !GOOGLE_MAPS_MODULE_LOAD_ERROR_PATTERN.test(exceptionValue)
  ) {
    return false;
  }

  return exception.stacktrace?.frames?.some((frame) =>
    frame.filename?.includes("maps-api-v3/")
  ) === true;
}

function isFetchFullSiteDataNetworkFailure(event: Sentry.Event): boolean {
  const exception = event.exception?.values?.[0];
  const exceptionValue = exception?.value;

  if (
    exception?.type !== "TypeError" ||
    typeof exceptionValue !== "string" ||
    !NETWORK_FETCH_FAILURE_PATTERN.test(exceptionValue)
  ) {
    return false;
  }

  return event.tags?.source === "fetchFullSiteData";
}

// An unrendered placeholder such as "${DSN}" counts as no DSN.
function isValidDsn(dsn: string): boolean {
  try {
    return /^https?:$/.test(new URL(dsn).protocol);
  } catch (e) {
    return false;
  }
}

export function initializeSentry(): void {
  if (hasInitialized) return;
  hasInitialized = true;

  // Report only with a valid DSN AND SENTRY_ENVIRONMENT=production (build-time).
  const dsn = import.meta.env.DSN || "";
  if (!isValidDsn(dsn) || import.meta.env.SENTRY_ENVIRONMENT !== "production") {
    return;
  }

  const environment = "production";
  const release = import.meta.env.SENTRY_RELEASE || "unknown";

  try {
    Sentry.init({
      dsn,
      environment,
      release,
      sendDefaultPii: false,
      ignoreErrors: [
        /Invalid call to runtime\.sendMessage/i,
        /Tab not found/i,
        /Extension context invalidated/i,
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications",
      ],
      denyUrls: [
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-(web-)?extension:\/\//i,
      ],
      beforeSend(event) {
        if (isKnownNonAppPromiseRejection(event)) {
          return null;
        }

        if (isGoogleMapsModuleLoadError(event)) {
          return null;
        }

        if (isFetchFullSiteDataNetworkFailure(event)) {
          return null;
        }

        return event;
      },
    });

  } catch (e) {
    // Keep app startup resilient if telemetry setup fails.
    console.warn("Sentry initialization failed:", e);
  }
}
